import type {
  OperationProgressMetric,
  OperationProgressModel,
} from '../../../shared/ui/progress';
import { isOperationProgressWaitingState } from '../../../shared/ui/progress';
import {
  formatValidationRunDuration,
  getValidationRunPercent,
  type ValidationRunPhase,
  type ValidationRunProgress,
} from './validationRunProgress';

const validationTitle = (phase: ValidationRunPhase): string => {
  if (phase === 'cache-hit') {
    return 'Validation report ready';
  }

  if (phase === 'complete') {
    return 'Validation complete';
  }

  if (phase === 'error') {
    return 'Validation stopped';
  }

  return 'Running PCF validation';
};

const validationPhaseLabel = (phase: ValidationRunPhase): string => {
  switch (phase) {
    case 'preparing':
      return 'Preparing';
    case 'syncing':
      return 'Checking setup';
    case 'submitting':
      return 'Starting Revit run';
    case 'running':
      return 'Checking model';
    case 'processing':
      return 'Preparing results';
    case 'complete':
      return 'Complete';
    case 'cache-hit':
      return 'Ready';
    case 'error':
      return 'Stopped';
    default:
      return 'Checking model';
  }
};

const validationStatus = (progress: ValidationRunProgress): OperationProgressModel['status'] => {
  if (isOperationProgressWaitingState(progress.state)) {
    return 'waiting';
  }

  const { phase } = progress;
  switch (phase) {
    case 'complete':
      return 'complete';
    case 'cache-hit':
      return 'cached';
    case 'error':
      return 'error';
    default:
      return 'running';
  }
};

const validationMessage = (progress: ValidationRunProgress): string => {
  switch (progress.phase) {
    case 'preparing':
      return 'Preparing validation run';
    case 'syncing':
      return 'Checking validation setup';
    case 'submitting':
      return 'Starting Revit validation';
    case 'running':
      return 'Checking model data';
    case 'processing':
      return 'Preparing validation results';
    case 'complete':
      return 'Validation results are ready';
    case 'cache-hit':
      return 'Using current validation report';
    case 'error':
      return 'Validation stopped';
    default:
      return progress.message || 'Running PCF validation';
  }
};

const selectedValidationCount = (progress: ValidationRunProgress): number | undefined => {
  const metric = progress.metrics?.find(
    ({ label }) => label.trim().toLowerCase() === 'checks selected'
  );
  const count = metric ? Number(metric.value) : Number.NaN;

  return Number.isInteger(count) && count > 0 ? count : undefined;
};

const validationCounts = (
  progress: ValidationRunProgress
): Pick<OperationProgressModel, 'processed' | 'total'> => {
  const total = selectedValidationCount(progress) ?? progress.total;
  const processed =
    progress.processed !== null && progress.processed !== undefined && total
      ? Math.min(progress.processed, total)
      : progress.processed;

  return { processed, total };
};

const validationDetail = (
  progress: ValidationRunProgress,
  counts: Pick<OperationProgressModel, 'processed' | 'total'>
): string | undefined => {
  if (progress.error) {
    return progress.error;
  }

  if (isOperationProgressWaitingState(progress.state) && progress.waitReason) {
    return progress.waitReason;
  }

  if (counts.processed !== null && counts.processed !== undefined && counts.total) {
    const noun = counts.total === 1 ? 'check' : 'checks';
    return `${counts.processed} of ${counts.total} ${noun} completed`;
  }

  switch (progress.phase) {
    case 'preparing':
      return 'Preparing selected checks for the active Revit model.';
    case 'syncing':
      return 'Confirming the current mapping and profile inputs.';
    case 'submitting':
      return 'Starting the validation run inside Revit.';
    case 'running':
      return 'Checking the selected validation rules against the active model.';
    case 'processing':
      return 'Organizing issues and refreshing the validation table.';
    case 'complete':
      return 'The results table is ready.';
    case 'cache-hit':
      return 'The model and validation setup have not changed.';
    default:
      return undefined;
  }
};

const validationEtaLabel = (progress: ValidationRunProgress, now: number): string | undefined => {
  if (progress.phase === 'cache-hit' || progress.phase === 'complete') {
    return 'Ready';
  }

  if (progress.phase === 'error') {
    return 'Stopped';
  }

  if (isOperationProgressWaitingState(progress.state)) {
    const waiting = formatValidationRunDuration(progress.waitElapsedMs);
    return waiting ? `Waiting ${waiting}` : 'Waiting';
  }

  const elapsedMs = progress.elapsedMs ?? Math.max(0, now - progress.startedAt);
  const elapsed = formatValidationRunDuration(elapsedMs);
  const eta = formatValidationRunDuration(progress.etaMs);

  if (eta && elapsed) {
    return `About ${eta} left / ${elapsed} elapsed`;
  }

  if (eta) {
    return `About ${eta} left`;
  }

  return elapsed ? `${elapsed} elapsed` : undefined;
};

const validationMetrics = (progress: ValidationRunProgress): OperationProgressMetric[] | undefined => {
  const issueMetric = progress.metrics?.find((metric) =>
    metric.label.trim().toLowerCase() === 'issues found'
  );

  return issueMetric
    ? [{ label: 'Issues found', value: issueMetric.value }]
    : undefined;
};

export function buildValidationRunProgressModel(
  progress: ValidationRunProgress,
  now: number = Date.now()
): OperationProgressModel {
  const waiting = isOperationProgressWaitingState(progress.state);
  const counts = validationCounts(progress);

  return {
    title: validationTitle(progress.phase),
    message: validationMessage(progress),
    detail: validationDetail(progress, counts),
    status: validationStatus(progress),
    operationId: progress.operationId,
    phase: validationPhaseLabel(progress.phase),
    state: progress.state,
    waitReason: progress.waitReason,
    waitElapsedMs: progress.waitElapsedMs,
    activeElapsedMs: progress.activeElapsedMs,
    processed: counts.processed,
    total: counts.total,
    percent: waiting ? null : getValidationRunPercent(progress, now),
    elapsedMs: progress.elapsedMs,
    etaMs: waiting ? undefined : progress.etaMs,
    resultReady: progress.resultReady,
    error: progress.error,
    etaLabel: validationEtaLabel(progress, now),
    locked: progress.phase !== 'complete' && progress.phase !== 'cache-hit' && progress.phase !== 'error',
    metrics: validationMetrics(progress),
  };
}
